import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAppSelector } from "@/store/hooks";
import { useProfile } from "@/hooks";
import { UserRole } from "@/interfaces/profile";
import Loader from "@/components/loader/Loader";

type Props = {
  allowedRoles?: UserRole[];
};

const ProtectedRoute = ({ allowedRoles }: Props) => {
  const location = useLocation();

  const { user, isLoading: authLoading } = useAppSelector(
    (state) => state.auth,
  );

  const userId = user?.id ?? "";

  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
  } = useProfile(userId);

  if (authLoading) {
    return <Loader type="bounce" />;
  }

  if (!user) {
    sessionStorage.setItem(
      "redirectAfterLogin",
      location.pathname + location.search,
    );

    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (profileLoading) {
    return <Loader type="bounce" />;
  }

  if (profileError || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!profile.isSecurityCodeVerified) {
    return (
      <Navigate
        to="/setup/transaction-password"
        replace
        state={{
          required: true,
          from: location,
        }}
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role as UserRole)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
